export interface Province {
  id: number;
  name: string;
}

export interface City {
  id: number;
  provinceId: number;
  name: string;
}

export interface District {
  id: number;
  cityId: number;
  name: string;
}

export interface Village {
  id: number;
  districtId: number;
  name: string;
}

export interface RegionParams {
  page?: number;
  size?: number;
  query?: string;
}

export function useRegions() {
  const { $customFetch } = useNuxtApp();

  /**
   * Fetch all provinces
   */
  const getProvinces = async (params: RegionParams = {}) => {
    try {
      const response = await $customFetch<{ data: Province[] }>(
        "/locations/provinces",
        {
          method: "GET",
          params: {
            ...params,
            page: 1,
            size: 1000,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching provinces:", error);
      throw error;
    }
  };

  /**
   * Fetch cities by province ID
   */
  const getCities = async (provinceId: number, params: RegionParams = {}) => {
    try {
      const response = await $customFetch<{ data: City[] }>(
        `/locations/cities`,
        {
          method: "GET",
          params: {
            ...params,
            provinceId,
            page: 1,
            size: 1000,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error(`Error fetching cities for province ${provinceId}:`, error);
      throw error;
    }
  };

  /**
   * Fetch districts by city ID
   */
  const getDistricts = async (cityId: number, params: RegionParams = {}) => {
    try {
      const response = await $customFetch<{ data: District[] }>(
        `/locations/districts`,
        {
          method: "GET",
          params: {
            ...params,
            cityId,
            page: 1,
            size: 1000,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error(`Error fetching districts for city ${cityId}:`, error);
      throw error;
    }
  };

  /**
   * Fetch villages by district ID
   */
  const getVillages = async (districtId: number, params: RegionParams = {}) => {
    try {
      const response = await $customFetch<{ data: Village[] }>(
        `/locations/villages`,
        {
          method: "GET",
          params: {
            ...params,
            districtId,
            page: 1,
            size: 1000,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error(
        `Error fetching villages for district ${districtId}:`,
        error,
      );
      throw error;
    }
  };

  return {
    getProvinces,
    getCities,
    getDistricts,
    getVillages,
  };
}
